/** Whether an ad-hoc stop drawn on the monitoring timeline can be added to its route. */
import { format } from "date-fns"

import type { Route } from "@/types/collections"

export interface AdHocStopValidation {
  isValid: boolean
  /** Null when the form is simply incomplete, which is not worth an error message. */
  error: string | null
}

export function validateAdHocStop(
  selection: { routeId: string; locationId: string },
  routes: Route[],
  times: { arrival: Date; departure: Date },
  now: Date
): AdHocStopValidation {
  if (!selection.routeId || !selection.locationId) {
    return { isValid: false, error: null }
  }

  const route = routes.find((r) => r.id === selection.routeId)
  if (!route) {
    return { isValid: false, error: "Selected route not found" }
  }

  const { arrival: newArrival, departure: newDeparture } = times

  if (newDeparture <= newArrival) {
    return { isValid: false, error: "Departure time must be after arrival time" }
  }

  if (newArrival < now) {
    return { isValid: false, error: "Arrival time cannot be in the past" }
  }

  for (const stop of route.stops) {
    const stopStart = stop.plannedArrival.getTime()
    const stopEnd = stop.plannedDeparture.getTime()
    const newStart = newArrival.getTime()
    const newEnd = newDeparture.getTime()

    if (newStart < stopEnd && newEnd > stopStart) {
      return {
        isValid: false,
        error: `This stop overlaps with "${stop.name}" (${format(stop.plannedArrival, "HH:mm")}-${format(stop.plannedDeparture, "HH:mm")})`,
      }
    }
  }

  return { isValid: true, error: null }
}
